import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, CheckSquare, Clock, PlayCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ActionPlans = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    descricao: '',
    responsavel: '',
    prazo: ''
  });

  useEffect(() => {
    fetchActions();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchActions();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, []);

  const fetchActions = async () => {
    try {
      const response = await axios.get(`${API_URL}/action-plans`);
      setActions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar planos de ação');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/action-plans`, formData);
      toast.success('Plano de ação criado com sucesso!');
      setOpen(false);
      setFormData({ descricao: '', responsavel: '', prazo: '' });
      fetchActions();
    } catch (error) {
      toast.error('Erro ao criar plano de ação');
    }
  };

  const updateStatus = async (actionId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/action-plans/${actionId}/status?status=${newStatus}`);
      toast.success('Status atualizado!');
      fetchActions();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pendente':
        return <Clock size={20} className="text-yellow-500" />;
      case 'em_execucao':
        return <PlayCircle size={20} className="text-blue-500" />;
      case 'concluido':
        return <CheckCircle2 size={20} className="text-emerald-500" />;
      default:
        return <Clock size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-500';
      case 'em_execucao':
        return 'bg-blue-500';
      case 'concluido':
        return 'bg-emerald-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pendente':
        return 'Pendente';
      case 'em_execucao':
        return 'Em Execução';
      case 'concluido':
        return 'Concluído';
      default:
        return status;
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  const pendingActions = actions.filter(a => a.status === 'pendente');
  const inProgressActions = actions.filter(a => a.status === 'em_execucao');
  const completedActions = actions.filter(a => a.status === 'concluido');

  return (
    <div className="space-y-6" data-testid="action-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Planos de Ação
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gerencie ações corretivas e preventivas
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="add-action-button"
            >
              <Plus size={20} className="mr-2" />
              Nova Ação
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="action-dialog">
            <DialogHeader>
              <DialogTitle>Criar Plano de Ação</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="descricao">Descrição da Ação</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  rows={3}
                  data-testid="action-descricao-input"
                />
              </div>
              <div>
                <Label htmlFor="responsavel">Responsável</Label>
                <Input
                  id="responsavel"
                  value={formData.responsavel}
                  onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                  required
                  data-testid="action-responsavel-input"
                />
              </div>
              <div>
                <Label htmlFor="prazo">Prazo</Label>
                <Input
                  id="prazo"
                  type="date"
                  value={formData.prazo}
                  onChange={(e) => setFormData({ ...formData, prazo: e.target.value })}
                  required
                  data-testid="action-prazo-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="action-submit-button">
                Criar Ação
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg">
              <Clock size={24} className="text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pendentes</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{pendingActions.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
              <PlayCircle size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Em Execução</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{inProgressActions.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-lg">
              <CheckCircle2 size={24} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Concluídas</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{completedActions.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pendente */}
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Clock size={20} className="text-yellow-500" />
            Pendentes ({pendingActions.length})
          </h3>
          <div className="space-y-3">
            {pendingActions.map((action) => (
              <Card key={action.id} className="p-4" data-testid={`action-card-${action.id}`}>
                <p className="font-medium text-slate-900 dark:text-white mb-2">{action.descricao}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  Responsável: {action.responsavel}
                </p>
                <p className="text-xs text-slate-500 mb-3">Prazo: {action.prazo}</p>
                <Select
                  value={action.status}
                  onValueChange={(value) => updateStatus(action.id, value)}
                >
                  <SelectTrigger data-testid={`status-select-${action.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="em_execucao">Em Execução</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </Card>
            ))}
          </div>
        </div>

        {/* Em Execução */}
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <PlayCircle size={20} className="text-blue-500" />
            Em Execução ({inProgressActions.length})
          </h3>
          <div className="space-y-3">
            {inProgressActions.map((action) => (
              <Card key={action.id} className="p-4 border-l-4 border-blue-500">
                <p className="font-medium text-slate-900 dark:text-white mb-2">{action.descricao}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  Responsável: {action.responsavel}
                </p>
                <p className="text-xs text-slate-500 mb-3">Prazo: {action.prazo}</p>
                <Select
                  value={action.status}
                  onValueChange={(value) => updateStatus(action.id, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="em_execucao">Em Execução</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </Card>
            ))}
          </div>
        </div>

        {/* Concluído */}
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <CheckCircle2 size={20} className="text-emerald-500" />
            Concluídas ({completedActions.length})
          </h3>
          <div className="space-y-3">
            {completedActions.map((action) => (
              <Card key={action.id} className="p-4 border-l-4 border-emerald-500 bg-slate-50 dark:bg-slate-900">
                <p className="font-medium text-slate-900 dark:text-white mb-2 line-through">
                  {action.descricao}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  Responsável: {action.responsavel}
                </p>
                <p className="text-xs text-slate-500">Prazo: {action.prazo}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {actions.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400">
            Nenhum plano de ação cadastrado ainda. Clique em "Nova Ação" para começar.
          </p>
        </Card>
      )}
    </div>
  );
};

export default ActionPlans;
